import { useEffect, useMemo, useState } from 'react';
import * as bd from 'react-basic-design';
import {
    TutorialDTO,
    saveTutorialApi,
    useDeleteTutorialApi,
    useSearchTutorialsApi,
} from '../../../api/tutorial-api';
import SvgAdd from '../../../assets/icons/Add';
import { InitFailed } from '../../../components/basic/init-failed';
import { Loading } from '../../../components/basic/loading';
import { N } from '../../../components/basic/n';
import { notify } from '../../../components/basic/notify';
import { T, translate } from '../../../components/basic/text';
import { LoadingButton } from '../../../components/loading-button';
import { PageNavbar } from '../../../components/ui/page-navbar';
import { TProvider } from '../../../i18n';
import { IBasicTableColumn } from '../../../interfaces/interfaces';
import { SaveTutorialModal } from './save-tutorial-modal';

export function TutorialApp() {
    const [showModal, setShowModal] = useState(false);
    const [selectedTutorial, setSelectedTutorial] = useState<TutorialDTO | undefined>();
    const tutorialInit = useSearchTutorialsApi();
    const deleteTutorial = useDeleteTutorialApi();

    const { result, error, busy, initialized, initializing } = tutorialInit;

    const columns: IBasicTableColumn<TutorialDTO>[] = useMemo(
        () => [
            {
                Header: <T>id</T>,
                name: 'id',
                width: 60,
                Cell: ({ row }) => <N>{row.id}</N>,
            },
            {
                Header: <T>title</T>,
                name: 'title',
                minWidth: 200,
            },
            {
                Header: <T>description</T>,
                name: 'description',
                width: 300,
                Cell: ({ row }) => {
                    if (!row.description) return <span>-</span>;
                    // Show HTML content with proper rendering
                    const preview = row.description.length > 200 ? row.description.substring(0, 200) + '...' : row.description;
                    return (
                        <div 
                            style={{ 
                                fontSize: '0.85rem', 
                                color: '#666',
                                lineHeight: '1.4',
                                maxHeight: '60px',
                                overflow: 'hidden'
                            }}
                            dangerouslySetInnerHTML={{ __html: preview }}
                        />
                    );
                },
            },
            {
                Header: <T>category</T>,
                name: 'category',
                width: 150,
            },
            {
                Header: <T>product</T>,
                name: 'productName',
                width: 150,
                Cell: ({ row }) => <span>{row.productName || '-'}</span>,
            },
            {
                Header: <T>youtube-id</T>,
                name: 'youtubeVideoId',
                width: 150,
            },
            {
                Header: <T>order</T>,
                name: 'order',
                width: 80,
                Cell: ({ row }) => <N>{row.order}</N>,
            },
            {
                Header: <T>views</T>,
                name: 'viewCount',
                width: 80,
                Cell: ({ row }) => <N>{row.viewCount || 0}</N>,
            },
            {
                Header: <T>published</T>,
                name: 'isPublished',
                width: 120,
                Cell: ({ row }) => (
                    <div className="flex items-center gap-2">
                        <bd.Badge variant={row.isPublished ? 'success' : 'secondary'} size="sm">
                            {row.isPublished ? translate('yes') : translate('no')}
                        </bd.Badge>
                        <bd.Button
                            size="sm"
                            variant="outline"
                            color={row.isPublished ? 'secondary' : 'primary'}
                            onClick={() => handleTogglePublish(row)}
                            className="text-xs px-2 py-1"
                        >
                            {row.isPublished ? translate('unpublish') : translate('publish')}
                        </bd.Button>
                    </div>
                ),
            },
        ],
        []
    );

    useEffect(() => {
        if (initialized || initializing || error) return;
        tutorialInit.post(null, {});
    }, [initialized, initializing, error, tutorialInit]);

    const handleDelete = (tutorial: TutorialDTO) => {
        bd.msgbox(
            translate('delete-tutorial'),
            translate('confirm-delete-tutorial'),
            [
                {
                    title: translate('delete'),
                    variant: 'contained',
                    color: 'secondary',
                    action: hide => {
                        deleteTutorial.remove(
                            tutorial,
                            () => {
                                tutorialInit.updateResult({
                                    ...result,
                                    tutorials: result.tutorials.filter(t => t.id !== tutorial.id),
                                });
                                notify.dark(translate('selected-tutorial-deleted-successfully'));
                                hide();
                                setShowModal(false);
                                setSelectedTutorial(undefined);
                            },
                            notify.error
                        );
                    },
                },
                {
                    title: translate('cancel'),
                    variant: 'outline',
                    color: 'secondary',
                },
            ],
            { className: 'modal-n', backdropClassName: 'modal-n' }
        );
    };

    const handleSave = (tutorial: TutorialDTO) => {
        saveTutorialApi(tutorial)
            .then(res => {
                if (selectedTutorial) {
                    tutorialInit.updateResult({
                        ...result,
                        tutorials: result.tutorials.map(t => (t.id === tutorial.id ? res.tutorial : t)),
                    });
                } else {
                    tutorialInit.updateResult({
                        ...result,
                        tutorials: [res.tutorial, ...result.tutorials],
                    });
                }

                setShowModal(false);
                setSelectedTutorial(undefined);
                notify.success(
                    selectedTutorial
                        ? translate('selected-tutorial-updated-successfully')
                        : translate('new-tutorial-created-successfully')
                );
            })
            .catch(notify.error);
    };

    const handleTogglePublish = (tutorial: TutorialDTO) => {
        const updatedTutorial = { ...tutorial, isPublished: !tutorial.isPublished };
        console.log('Sending tutorial data:', updatedTutorial);
        saveTutorialApi(updatedTutorial)
            .then(res => {
                console.log('Save tutorial response:', res);
                tutorialInit.updateResult({
                    ...result,
                    tutorials: result.tutorials.map(t => (t.id === tutorial.id ? res.tutorial : t)),
                });
                notify.success(
                    updatedTutorial.isPublished 
                        ? translate('tutorial-published-successfully')
                        : translate('tutorial-unpublished-successfully')
                );
            })
            .catch(error => {
                console.error('Save tutorial error:', error);
                notify.error(error);
            });
    };

    if (error) return <InitFailed ex={error} onRetry={() => tutorialInit.post(null, {})} />;
    if (!initialized || initializing) return <Loading />;

    return (
        <TProvider>
            <div className="container">
                <PageNavbar
                    onGoBack="/home"
                    title="tutorials-management"
                    controls={
                        <LoadingButton
                            variant="contained"
                            size="sm"
                            label={<T>new-tutorial</T>}
                            icon={<SvgAdd />}
                            onClick={() => {
                                setShowModal(true);
                                setSelectedTutorial(undefined);
                            }}
                            loading={busy}
                        />
                    }
                />
                <bd.Paper className="h-100">
                    <bd.BasicTable
                        cellCSS={({ row }) => {
                            if (!row.isPublished) return 'bg-gray-100 opacity-50';
                            return '';
                        }}
                        columns={columns}
                        data={result.tutorials}
                        onRowClick={({ row }) => {
                            setSelectedTutorial(row);
                            setShowModal(true);
                        }}
                        hover
                    >
                        <T as="div" className="nothing-found">
                            nothing-found
                        </T>
                    </bd.BasicTable>

                    <SaveTutorialModal
                        show={showModal}
                        onHide={() => {
                            setShowModal(false);
                            setSelectedTutorial(undefined);
                        }}
                        onSave={handleSave}
                        onDelete={handleDelete}
                        products={result.products || []}
                        selectedTutorial={selectedTutorial}
                        allowEdit={result.allowEdit}
                    />
                </bd.Paper>
            </div>
        </TProvider>
    );
}

export default TutorialApp;

